import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, TrendingUp, Users, Activity, AlertCircle, FileBarChart, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Cell,
  LabelList
} from 'recharts';

export const OncologySurvivalCaseStudy = () => {
  const navigate = useNavigate();

  // Table 1: Five analytical methods
  const analyticalMethods = [
    {
      method: "Kaplan-Meier + Log-rank",
      question: "How does survival differ by stage, receptor status, and treatment?",
      rPackage: "survival, survminer"
    },
    {
      method: "Cox Proportional Hazards",
      question: "Which clinical factors independently predict OS and DFS, and by how much?",
      rPackage: "survival, rms, broom"
    },
    {
      method: "Competing Risks (Fine-Gray)",
      question: "Does Stage II increase cancer-specific death after accounting for death from other causes?",
      rPackage: "cmprsk"
    },
    {
      method: "Time-varying Cox",
      question: "Does rising CA 15-3 during follow-up predict subsequent death?",
      rPackage: "survival (tstart/tstop format)"
    },
    {
      method: "Linear Mixed Effects (LME)",
      question: "How does CA 15-3 change over time, and does trajectory differ by treatment?",
      rPackage: "lme4, nlme"
    }
  ];

  // Table 2: Dataset structure
  const datasetStructure = [
    {
      dataset: "Master Dataset",
      rows: "600 × 18",
      description: "One row per patient. Baseline characteristics, OS, DFS, and event type."
    },
    {
      dataset: "Time-varying Dataset",
      rows: "~1,800 rows",
      description: "Counting process format (tstart/tstop). One interval per biomarker measurement window."
    },
    {
      dataset: "Longitudinal Dataset",
      rows: "~2,400 rows",
      description: "Long format. Repeated CA 15-3 measurements at 0, 3, 6, 12, 24 months per patient."
    }
  ];

  // Table 3: Variable dictionary (selected key variables)
  const variableDictionary = [
    {
      variable: "age",
      type: "Continuous",
      description: "Age at diagnosis (years). Range 25–80, mean ~52."
    },
    {
      variable: "stage",
      type: "Categorical",
      description: "Cancer Stage I or II. 45% Stage I, 55% Stage II in this cohort."
    },
    {
      variable: "grade",
      type: "Ordinal",
      description: "Histological grade G1/G2/G3. Higher grade = more aggressive tumour biology."
    },
    {
      variable: "er_status",
      type: "Binary",
      description: "Receptor status (Positive/Negative). Positive status associated with better prognosis."
    },
    {
      variable: "her2_status",
      type: "Binary",
      description: "HER2 receptor status. Positive status associated with more aggressive disease."
    },
    {
      variable: "treatment",
      type: "Categorical",
      description: "Treatment received: Hormone / Chemo / Chemo+Hormone / None."
    },
    {
      variable: "nodes_pos",
      type: "Count",
      description: "Number of positive lymph nodes. Strong prognostic indicator."
    },
    {
      variable: "tumor_size",
      type: "Continuous",
      description: "Tumour size in cm at diagnosis."
    },
    {
      variable: "ca153_base",
      type: "Continuous",
      description: "Baseline CA 15-3 serum marker (U/mL). Tumour marker used for treatment monitoring."
    },
    {
      variable: "os_time / os_event",
      type: "Survival",
      description: "Overall survival time (months) and event indicator (1=death, 0=censored). Max 10 years."
    }
  ];

  // Kaplan-Meier data - OS by Stage
  const kmDataStage = [
    { time: 0, stageI: 1.0, stageII: 1.0 },
    { time: 6, stageI: 0.98, stageII: 0.95 },
    { time: 12, stageI: 0.94, stageII: 0.88 },
    { time: 18, stageI: 0.89, stageII: 0.78 },
    { time: 24, stageI: 0.84, stageII: 0.68 },
    { time: 30, stageI: 0.79, stageII: 0.58 },
    { time: 36, stageI: 0.74, stageII: 0.49 },
    { time: 42, stageI: 0.69, stageII: 0.41 },
    { time: 48, stageI: 0.64, stageII: 0.34 },
    { time: 60, stageI: 0.55, stageII: 0.24 },
    { time: 72, stageI: 0.47, stageII: 0.17 },
    { time: 84, stageI: 0.40, stageII: 0.12 },
    { time: 96, stageI: 0.34, stageII: 0.08 },
    { time: 108, stageI: 0.29, stageII: 0.06 },
    { time: 120, stageI: 0.25, stageII: 0.04 }
  ];

  // KM data - DFS by ER Status
  const kmDataER = [
    { time: 0, erPositive: 1.0, erNegative: 1.0 },
    { time: 6, erPositive: 0.96, erNegative: 0.89 },
    { time: 12, erPositive: 0.91, erNegative: 0.78 },
    { time: 18, erPositive: 0.86, erNegative: 0.68 },
    { time: 24, erPositive: 0.81, erNegative: 0.58 },
    { time: 30, erPositive: 0.76, erNegative: 0.49 },
    { time: 36, erPositive: 0.71, erNegative: 0.41 },
    { time: 42, erPositive: 0.66, erNegative: 0.34 },
    { time: 48, erPositive: 0.61, erNegative: 0.28 },
    { time: 60, erPositive: 0.52, erNegative: 0.19 },
    { time: 72, erPositive: 0.44, erNegative: 0.13 }
  ];

  // KM data - OS by Treatment
  const kmDataTreatment = [
    { time: 0, hormone: 1.0, chemo: 1.0, combined: 1.0, none: 1.0 },
    { time: 6, hormone: 0.97, chemo: 0.96, combined: 0.98, none: 0.89 },
    { time: 12, hormone: 0.92, chemo: 0.91, combined: 0.95, none: 0.76 },
    { time: 18, hormone: 0.86, chemo: 0.85, combined: 0.91, none: 0.64 },
    { time: 24, hormone: 0.80, chemo: 0.79, combined: 0.86, none: 0.53 },
    { time: 30, hormone: 0.74, chemo: 0.73, combined: 0.81, none: 0.43 },
    { time: 36, hormone: 0.68, chemo: 0.67, combined: 0.76, none: 0.34 },
    { time: 48, hormone: 0.57, chemo: 0.56, combined: 0.67, none: 0.21 },
    { time: 60, hormone: 0.47, chemo: 0.46, combined: 0.58, none: 0.12 },
    { time: 72, hormone: 0.38, chemo: 0.37, combined: 0.50, none: 0.07 }
  ];

  // Table 4: KM Summary
  const kmSummaryTable = [
    {
      figure: "Figure 1",
      comparison: "OS by Stage I vs II",
      test: "Log-rank",
      pValue: "< 0.001",
      medianOS_I: "86.4 months",
      medianOS_II: "52.1 months",
      fiveYearOS_I: "64%",
      fiveYearOS_II: "34%"
    },
    {
      figure: "Figure 2",
      comparison: "DFS by ER Status",
      test: "Log-rank",
      pValue: "< 0.001",
      medianDFS_Pos: "78.2 months",
      medianDFS_Neg: "38.7 months",
      threeYearDFS_Pos: "76%",
      threeYearDFS_Neg: "49%"
    },
    {
      figure: "Figure 3",
      comparison: "OS by Treatment (4-way)",
      test: "Log-rank (χ² = 42.8, df=3)",
      pValue: "< 0.001",
      note: "Chemo+Hormone shows superior survival vs None (HR direction: protective)"
    }
  ];

  // Cox PH Model Results - Table 5
  const coxResults = [
    { variable: "Stage II (vs I)", hr: 2.34, ci_lower: 1.76, ci_upper: 3.12, ci: "1.76–3.12", pValue: "< 0.001" },
    { variable: "Age (per year)", hr: 1.03, ci_lower: 1.01, ci_upper: 1.05, ci: "1.01–1.05", pValue: "0.002" },
    { variable: "Grade G3 (vs G1)", hr: 1.89, ci_lower: 1.34, ci_upper: 2.67, ci: "1.34–2.67", pValue: "< 0.001" },
    { variable: "ER Positive (vs Negative)", hr: 0.62, ci_lower: 0.47, ci_upper: 0.82, ci: "0.47–0.82", pValue: "< 0.001" },
    { variable: "HER2 Positive (vs Negative)", hr: 1.45, ci_lower: 1.12, ci_upper: 1.88, ci: "1.12–1.88", pValue: "0.005" },
    { variable: "Chemo+Hormone (vs None)", hr: 0.48, ci_lower: 0.34, ci_upper: 0.68, ci: "0.34–0.68", pValue: "< 0.001" },
    { variable: "Nodes positive (per node)", hr: 1.18, ci_lower: 1.10, ci_upper: 1.27, ci: "1.10–1.27", pValue: "< 0.001" },
    { variable: "Tumour size (per cm)", hr: 1.12, ci_lower: 1.05, ci_upper: 1.20, ci: "1.05–1.20", pValue: "< 0.001" }
  ];

  const cStatistic = 0.78;

  // Competing Risks - CIF data by stage
  const cifData = [
    { time: 0, stageI_cancer: 0, stageI_other: 0, stageII_cancer: 0, stageII_other: 0 },
    { time: 12, stageI_cancer: 0.04, stageI_other: 0.02, stageII_cancer: 0.09, stageII_other: 0.03 },
    { time: 24, stageI_cancer: 0.10, stageI_other: 0.05, stageII_cancer: 0.22, stageII_other: 0.08 },
    { time: 36, stageI_cancer: 0.17, stageI_other: 0.09, stageII_cancer: 0.35, stageII_other: 0.13 },
    { time: 48, stageI_cancer: 0.24, stageI_other: 0.12, stageII_cancer: 0.46, stageII_other: 0.17 },
    { time: 60, stageI_cancer: 0.31, stageI_other: 0.15, stageII_cancer: 0.55, stageII_other: 0.20 },
    { time: 72, stageI_cancer: 0.37, stageI_other: 0.17, stageII_cancer: 0.62, stageII_other: 0.22 },
    { time: 84, stageI_cancer: 0.42, stageI_other: 0.18, stageII_cancer: 0.67, stageII_other: 0.23 },
    { time: 96, stageI_cancer: 0.46, stageI_other: 0.19, stageII_cancer: 0.71, stageII_other: 0.24 },
    { time: 108, stageI_cancer: 0.49, stageI_other: 0.20, stageII_cancer: 0.74, stageII_other: 0.25 },
    { time: 120, stageI_cancer: 0.52, stageI_other: 0.21, stageII_cancer: 0.76, stageII_other: 0.26 }
  ];

  // Table 6: Cause-specific Cox vs Fine-Gray
  const competingRisksComparison = [
    {
      approach: "Cause-specific Cox",
      estimates: "Hazard of cancer death among those still alive and event-free",
      whenToUse: "Understanding biological/aetiological effect of a covariate on cancer death specifically"
    },
    {
      approach: "Fine-Gray",
      estimates: "Sub-distribution Hazard based on cumulative incidence function (CIF)",
      whenToUse: "Predicting absolute risk and informing clinical decisions about who needs preventive intervention"
    }
  ];

  // Time-varying CA 15-3 data
  const timeVaryingData = [
    { time: 0, ca153_low: 22, ca153_medium: 28, ca153_high: 35 },
    { time: 3, ca153_low: 23, ca153_medium: 31, ca153_high: 42 },
    { time: 6, ca153_low: 24, ca153_medium: 34, ca153_high: 51 },
    { time: 12, ca153_low: 25, ca153_medium: 39, ca153_high: 64 },
    { time: 18, ca153_low: 26, ca153_medium: 45, ca153_high: 79 },
    { time: 24, ca153_low: 27, ca153_medium: 52, ca153_high: 96 }
  ];

  // LME - Predicted trajectories by treatment
  const lmeTrajectories = [
    { time: 0, hormone: 118, chemo: 120, combined: 119, none: 122 },
    { time: 3, hormone: 112, chemo: 110, combined: 105, none: 125 },
    { time: 6, hormone: 106, chemo: 101, combined: 92, none: 128 },
    { time: 12, hormone: 95, chemo: 85, combined: 72, none: 135 },
    { time: 18, hormone: 86, chemo: 73, combined: 58, none: 143 },
    { time: 24, hormone: 78, chemo: 64, combined: 48, none: 152 }
  ];

  // Table 8: Model hierarchy
  const modelHierarchy = [
    {
      model: "M0",
      formula: "ca153 ~ 1 + (1|id)",
      purpose: "Null / Intercept only. Estimate ICC (Intraclass Correlation Coefficient)"
    },
    {
      model: "M1",
      formula: "ca153 ~ time + (1|id)",
      purpose: "Random intercept + time. Average CA 15-3 trend"
    },
    {
      model: "M2",
      formula: "ca153 ~ time*treatment + age + stage + er_status + (time|id)",
      purpose: "Full model. Treatment × time interaction. Random intercept & slope"
    },
    {
      model: "M3",
      formula: "AR(1) residuals (nlme) + corAR1(form = ~time|id)",
      purpose: "Test if residuals within a patient are autocorrelated over time"
    }
  ];

  // Table 9: Integrated clinical narrative
  const integratedNarrative = [
    {
      analysis: "KM. OS by Stage",
      clinicalAnswer: "Stage II patients have meaningfully lower 5- and 10-year OS. Survival curves diverge from ~18 months. Log-rank p quantifies significance."
    },
    {
      analysis: "KM. DFS by ER Status",
      clinicalAnswer: "ER-positive patients show longer disease-free intervals, consistent with hormone therapy benefit. ER status is the single strongest prognostic binary marker."
    },
    {
      analysis: "Cox PH. Multivariable OS",
      clinicalAnswer: "After adjusting for all covariates, Stage II, Grade 3, HER2+, and positive nodes independently increase hazard. Chemo+Hormone significantly reduces hazard vs no treatment."
    },
    {
      analysis: "Competing Risks",
      clinicalAnswer: "CIF analysis shows the proportion of deaths attributable to cancer vs other causes. Fine-Gray sHR for Stage II on cancer death may differ from Cox HR. illustrating the risk of misclassifying competing events as censored."
    },
    {
      analysis: "Time-varying Cox (CA 15-3)",
      clinicalAnswer: "Rising CA 15-3 during follow-up is an independent predictor of subsequent death, over and above baseline clinical characteristics. This validates CA 15-3 as a dynamic monitoring marker."
    },
    {
      analysis: "LME. CA 15-3 Trajectories",
      clinicalAnswer: "CA 15-3 declines over 24 months in patients receiving Chemo+Hormone, rises in untreated patients. The treatment × time interaction is statistically significant, confirming differential biomarker response."
    }
  ];

  // Table 10: R Package Reference
  const rPackages = [
    { package: "survival", analysis: "KM, Cox PH, TV Cox", keyFunctions: "Surv(), survfit(), coxph(), cox.zph()" },
    { package: "survminer", analysis: "KM plots", keyFunctions: "ggsurvplot(), ggcoxzph()" },
    { package: "cmprsk", analysis: "Competing risks", keyFunctions: "cuminc(), crr()" },
    { package: "lme4", analysis: "Mixed effects", keyFunctions: "lmer(), fixef(), ranef(), VarCorr()" },
    { package: "nlme", analysis: "LME with correlation", keyFunctions: "lme(), corAR1()" },
    { package: "broom", analysis: "Tidy model output", keyFunctions: "tidy(), glance(), augment()" },
    { package: "rms", analysis: "Discrimination & calibration", keyFunctions: "cph(), val.surv(), calibrate()" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 pt-16 md:pt-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-cyan-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <button
            onClick={() => navigate('/case-studies')}
            className="flex items-center gap-2 text-blue-100 hover:text-white mb-6 transition-colors group"
          >
            <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
            Back to Case Studies
          </button>

          <div className="flex items-start justify-between">
            <div className="flex-1">
              <Badge className="mb-4 bg-cyan-500 hover:bg-cyan-600 text-white border-0">
                Hospital Research Support · Advanced Biostatistics · Survival Analysis · Clinical Research
              </Badge>
              <h1 className="text-4xl sm:text-5xl font-bold mb-4 leading-tight">
                Publication-Ready Oncology Research: A Complete Biostatistical Framework for UAE Hospital Research Teams
              </h1>
              <p className="text-lg sm:text-xl text-blue-100 mb-6 leading-relaxed max-w-4xl">
                Five advanced statistical methods applied to a 600-patient oncology cohort. demonstrating the analytical framework that gets clinical research published in peer-reviewed journals.
              </p>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center gap-2 bg-blue-800/30 px-4 py-2 rounded-lg backdrop-blur-sm">
                  <Users className="h-4 w-4" />
                  <span>600 patients</span>
                </div>
                <div className="flex items-center gap-2 bg-blue-800/30 px-4 py-2 rounded-lg backdrop-blur-sm">
                  <Activity className="h-4 w-4" />
                  <span>10-year follow-up</span>
                </div>
                <div className="flex items-center gap-2 bg-blue-800/30 px-4 py-2 rounded-lg backdrop-blur-sm">
                  <BarChart3 className="h-4 w-4" />
                  <span>5 Advanced Methods</span>
                </div>
              </div>
            </div>
          </div>

          {/* Simulated Data Disclaimer */}
          <div className="mt-6 bg-yellow-500/20 border border-yellow-300/30 rounded-lg p-4 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-200 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-50 font-medium">Simulated Data for Demonstration</p>
                <p className="text-xs text-yellow-100 mt-1">
                  All datasets, statistical outputs, and visualizations use synthetically generated data for portfolio and educational purposes. Results are illustrative of advanced biostatistical methodology.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* At a Glance */}
        <section className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">At a Glance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="border-2 border-cyan-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Who This Is For</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Hospital research teams, clinical investigators, oncology departments, and academic researchers in the UAE preparing studies for peer-reviewed publication. who need advanced survival analysis executed at international journal standard.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-blue-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">What This Demonstrates</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  A five-method analytical framework. Kaplan-Meier estimation, multivariable Cox regression, competing risks analysis, time-varying covariate modelling, and linear mixed-effects modelling. applied sequentially to a 600-patient cohort with 10-year follow-up. The complete biostatistics toolkit for rigorous oncology research.
                </p>
              </CardContent>
            </Card>
            <Card className="border-2 border-purple-200">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Why It Matters for Your Research</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  Peer reviewers reject oncology papers most frequently for statistical reasons. inadequate handling of competing risks, baseline-only biomarker models, missing proportional hazards validation. This framework addresses every one of these proactively, before submission.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* The Research Context */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur border-l-4 border-cyan-600">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">The Research Context</h2>
            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                UAE hospitals are generating increasingly rich clinical datasets. oncology registries, longitudinal patient follow-up data, biomarker measurement series, treatment outcome records. The clinical expertise to generate this data exists within hospital research teams. What is frequently missing is the statistical methodology to turn it into publication-ready research.
              </p>
              <p>
                The gap is specific and consistent: clinical teams know what questions they want to answer. They do not always have access to the advanced biostatistical methods. competing risks analysis, time-varying covariates, longitudinal mixed-effects modelling. that peer-reviewed journals in oncology now expect as standard.
              </p>
              <p>
                The consequence is research that stalls before publication, or reaches peer review and is returned with statistical revision requests that delay publication by months.
              </p>
              <p className="font-semibold text-gray-900">
                This case study demonstrates the complete analytical framework StatZen Analytics applies to oncology survival research. using a synthetic dataset of 600 early-stage oncology patients with 10-year follow-up. showing exactly what becomes possible when clinical data meets rigorous biostatistical methodology.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* The Research Problem */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Standard Statistical Approaches Are Not Enough</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Oncology research has specific statistical challenges that standard approaches handle poorly. Each challenge has a methodological solution. and using the wrong method produces biased estimates that peer reviewers will identify and reject.
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full bg-white rounded-lg shadow-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">The Challenge</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Why Standard Methods Fail</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">The Correct Approach</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-700">Patients can die from causes other than cancer. cardiovascular disease, other malignancies, complications</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Standard Cox regression treats non-cancer deaths as censored observations. as if the patient simply left the study. This systematically overestimates cancer-specific mortality, particularly in older cohorts</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Competing risks analysis (Fine-Gray model) explicitly accounts for the fact that a patient who dies of cardiovascular disease can no longer die of cancer. producing accurate absolute risk estimates</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-700">Biomarkers like CA 15-3 change dynamically during treatment. rising before recurrence, falling with successful therapy</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Baseline-only Cox models fix biomarker values at the start of follow-up and ignore subsequent changes. This misses the dynamic information that often most strongly predicts outcomes</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Time-varying covariate analysis splits follow-up into intervals and updates biomarker values at each measurement. capturing the predictive signal in biomarker trajectories rather than just baseline values</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-700">Repeated measurements from the same patient over time are correlated. not independent observations</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Standard linear regression assumes all observations are independent. Applying it to repeated measures underestimates standard errors and inflates statistical significance. producing findings that will not replicate</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Linear mixed-effects models account for within-patient correlation through random effects. allowing each patient to have their own baseline and trajectory while estimating population-level treatment effects correctly</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-4 text-sm text-gray-700">Survival curves need to be compared across groups while controlling for confounding factors</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Kaplan-Meier curves show unadjusted survival differences. they cannot control for age, stage, grade, and treatment simultaneously</td>
                    <td className="px-4 py-4 text-sm text-gray-700">Multivariable Cox proportional hazards regression adjusts for all covariates simultaneously. producing hazard ratios that represent the independent effect of each factor</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <Card className="border-2 border-amber-400 bg-amber-50">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">The methodological standard peer reviewers expect:</h3>
                <p className="text-gray-700 leading-relaxed">
                  High-impact oncology journals now expect competing risks analysis whenever non-cancer deaths are a realistic outcome, time-varying analysis when biomarkers are measured longitudinally, and proportional hazards assumption testing for every Cox model. Submitting without these. or using standard Cox regression when Fine-Gray is appropriate. is one of the most common reasons for statistical rejection at the revision stage.
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>

        {/* The Analytical Framework */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <p className="text-sm font-semibold text-cyan-600 mb-2">THE ANALYSIS</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Five Methods. Five Clinical Questions</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Each method in this framework was chosen to answer a specific clinical question that the other methods cannot. They are applied sequentially. each building on the previous. to produce a complete, multi-layered picture of survival, prognosis, treatment efficacy, and biomarker dynamics.
            </p>
          </CardContent>
        </Card>

        {/* Problem Statement */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur border-l-4 border-blue-600">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Problem Statement & Research Context</h2>
            <div className="space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                Oncology research demands sophisticated statistical approaches to answer complex clinical questions about survival, 
                prognosis, and treatment efficacy. Traditional single-method analyses often provide incomplete insights, failing to 
                account for competing risks, time-varying biomarker dynamics, or repeated measurements inherent in longitudinal cancer care.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Key Challenges:</strong>
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span>Survival analyses must distinguish cancer-specific mortality from competing causes (e.g., cardiovascular death), yet standard Cox regression treats these as censored events, leading to biased estimates</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span>Baseline-only models ignore dynamic biomarker changes (e.g., rising tumor markers) that often precede clinical events</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span>Treatment efficacy cannot be fully assessed without examining longitudinal biomarker trajectories and differential response patterns</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span>Multivariable risk models require careful attention to proportional hazards assumptions, collinearity, and model discrimination</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed">
                This case study addresses these challenges by integrating <strong>five complementary statistical methods</strong> 
                into a comprehensive analytical framework, demonstrating how methodological triangulation provides more robust 
                clinical insights than any single approach alone.
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Executive Summary */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl">
                <FileBarChart className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Executive Summary</h2>
            </div>
            
            <div className="prose prose-blue max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                This case study demonstrates a comprehensive survival and longitudinal analysis of early-stage oncology patients using a synthetic dataset of 600 patients. The analysis employs a sequential approach with <strong>five advanced statistical methods</strong> to address specific clinical questions that simpler methods cannot answer.
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                The core objective is to provide a robust and multi-faceted understanding of prognostic factors, treatment effects, and disease monitoring markers. Each method builds upon the previous, creating a complete analytical framework for oncology research.
              </p>

              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sequential Analytical Approach</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                    <div>
                      <p className="font-medium text-gray-900">Kaplan-Meier Estimation</p>
                      <p className="text-sm text-gray-600">Non-parametric survival curves stratified by clinical factors</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">2</div>
                    <div>
                      <p className="font-medium text-gray-900">Cox Proportional Hazards Regression</p>
                      <p className="text-sm text-gray-600">Multivariable modeling to identify independent risk factors</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">3</div>
                    <div>
                      <p className="font-medium text-gray-900">Competing Risks Analysis</p>
                      <p className="text-sm text-gray-600">Accounts for death from other causes to prevent bias</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">4</div>
                    <div>
                      <p className="font-medium text-gray-900">Time-varying Covariate Analysis</p>
                      <p className="text-sm text-gray-600">Captures dynamic biomarker changes during follow-up</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold">5</div>
                    <div>
                      <p className="font-medium text-gray-900">Linear Mixed-Effects Modeling</p>
                      <p className="text-sm text-gray-600">Longitudinal biomarker trajectories by treatment group</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table 1: Five Analytical Methods */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Table 1: Five Analytical Methods and Their Clinical Purpose</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Method</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">Clinical Question Answered</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-900">R Package</th>
                  </tr>
                </thead>
                <tbody>
                  {analyticalMethods.map((method, idx) => (
                    <tr key={idx} className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
                      <td className="py-3 px-4 font-medium text-gray-900">{method.method}</td>
                      <td className="py-3 px-4 text-gray-700">{method.question}</td>
                      <td className="py-3 px-4 text-gray-600 font-mono text-xs">{method.rPackage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Study Design & Data */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl">
                <Users className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Study Design & Data</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.1 Data Source</h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span><strong>Synthetic dataset</strong> (n=600) simulating real-world early-stage oncology registry</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Captures clinical, pathological, treatment characteristics, biomarker measurements, and survival follow-up</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>All data synthetically generated for portfolio and educational purposes</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span>Reproducible with <code className="bg-white px-2 py-1 rounded text-sm font-mono">set.seed(2026)</code></span>
                    </li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.2 Dataset Structure</h3>
                <p className="text-gray-700 mb-4">Three linked datasets used across the five analyses:</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Dataset</th>
                        <th className="text-left py-3 px-4 font-semibold">Rows × Cols</th>
                        <th className="text-left py-3 px-4 font-semibold">Description</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {datasetStructure.map((ds, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0">
                          <td className="py-3 px-4 font-medium text-gray-900">{ds.dataset}</td>
                          <td className="py-3 px-4 text-gray-700 font-mono text-xs">{ds.rows}</td>
                          <td className="py-3 px-4 text-gray-700">{ds.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">2.3 Complete Variable Dictionary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Variable</th>
                        <th className="text-left py-3 px-4 font-semibold">Type</th>
                        <th className="text-left py-3 px-4 font-semibold">Description & Clinical Relevance</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {variableDictionary.map((variable, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0 hover:bg-blue-50 transition-colors">
                          <td className="py-3 px-4 font-mono text-xs text-blue-700 font-medium">{variable.variable}</td>
                          <td className="py-3 px-4 text-gray-700">
                            <Badge variant="outline" className="text-xs">{variable.type}</Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-700">{variable.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Method 1: Kaplan-Meier Analysis */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">3. Kaplan-Meier Survival Analysis</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.1 Method Overview</h3>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Kaplan-Meier estimation</strong> is a non-parametric method for estimating the survival function from lifetime data. 
                    It handles censored observations (patients lost to follow-up or alive at study end) by calculating the probability of surviving 
                    past each event time, multiplying these conditional probabilities to produce survival curves.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">3.2 Statistical Testing</h3>
                <p className="text-gray-700 mb-4">
                  <strong>Log-rank test</strong> compares survival curves between groups. A p-value &lt; 0.05 indicates a statistically significant 
                  difference in survival distributions. The test is sensitive to differences occurring throughout follow-up.
                </p>
              </div>

              {/* Figure 1: OS by Stage */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 1: Overall Survival by Cancer Stage</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={kmDataStage} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="time" 
                        label={{ value: 'Time (Months)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        domain={[0, 1]}
                        ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        label={{ value: 'Survival Probability', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value) => (value * 100).toFixed(1) + '%'}
                        labelFormatter={(label) => `Month ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="stageI" 
                        stroke="#2563eb" 
                        strokeWidth={3}
                        name="Stage I"
                        dot={false}
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="stageII" 
                        stroke="#dc2626" 
                        strokeWidth={3}
                        name="Stage II"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Log-rank test: χ² = 38.5, p &lt; 0.001. Median OS: Stage I = 86.4 months, Stage II = 52.1 months
                  </p>
                </div>
              </div>

              {/* Figure 2: DFS by ER Status */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 2: Disease-Free Survival by Receptor Status</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={kmDataER} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="time" 
                        label={{ value: 'Time (Months)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        domain={[0, 1]}
                        ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        label={{ value: 'Disease-Free Survival Probability', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value) => (value * 100).toFixed(1) + '%'}
                        labelFormatter={(label) => `Month ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="erPositive" 
                        stroke="#059669" 
                        strokeWidth={3}
                        name="ER Positive"
                        dot={false}
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="erNegative" 
                        stroke="#dc2626" 
                        strokeWidth={3}
                        name="ER Negative"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Log-rank test: χ² = 42.1, p &lt; 0.001. Median DFS: ER+ = 78.2 months, ER- = 38.7 months
                  </p>
                </div>
              </div>

              {/* Figure 3: OS by Treatment */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 3: Overall Survival by Treatment Group</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={kmDataTreatment} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="time" 
                        label={{ value: 'Time (Months)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        domain={[0, 1]}
                        ticks={[0, 0.2, 0.4, 0.6, 0.8, 1.0]}
                        label={{ value: 'Survival Probability', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value) => (value * 100).toFixed(1) + '%'}
                        labelFormatter={(label) => `Month ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="hormone" 
                        stroke="#3b82f6" 
                        strokeWidth={2.5}
                        name="Hormone"
                        dot={false}
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="chemo" 
                        stroke="#8b5cf6" 
                        strokeWidth={2.5}
                        name="Chemo"
                        dot={false}
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="combined" 
                        stroke="#059669" 
                        strokeWidth={3}
                        name="Chemo+Hormone"
                        dot={false}
                      />
                      <Line 
                        type="stepAfter" 
                        dataKey="none" 
                        stroke="#dc2626" 
                        strokeWidth={2.5}
                        name="None"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Log-rank test: χ² = 42.8 (df=3), p &lt; 0.001. Combined therapy shows superior survival
                  </p>
                </div>
              </div>

              {/* Table 4: KM Summary */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table 4: Kaplan-Meier Analyses Summary</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Figure</th>
                        <th className="text-left py-3 px-4 font-semibold">Comparison</th>
                        <th className="text-left py-3 px-4 font-semibold">Test</th>
                        <th className="text-left py-3 px-4 font-semibold">p-value</th>
                        <th className="text-left py-3 px-4 font-semibold">Key Metrics</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      <tr className="border-b border-gray-200">
                        <td className="py-3 px-4 font-medium">Fig 1</td>
                        <td className="py-3 px-4">OS by Stage I vs II</td>
                        <td className="py-3 px-4">Log-rank</td>
                        <td className="py-3 px-4 font-semibold text-red-600">&lt; 0.001</td>
                        <td className="py-3 px-4 text-xs">Median OS: I=86.4mo, II=52.1mo; 5yr OS: I=64%, II=34%</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-3 px-4 font-medium">Fig 2</td>
                        <td className="py-3 px-4">DFS by ER Status</td>
                        <td className="py-3 px-4">Log-rank</td>
                        <td className="py-3 px-4 font-semibold text-red-600">&lt; 0.001</td>
                        <td className="py-3 px-4 text-xs">Median DFS: ER+=78.2mo, ER-=38.7mo; 3yr DFS: ER+=76%, ER-=49%</td>
                      </tr>
                      <tr>
                        <td className="py-3 px-4 font-medium">Fig 3</td>
                        <td className="py-3 px-4">OS by Treatment (4-way)</td>
                        <td className="py-3 px-4">Log-rank (χ²=42.8, df=3)</td>
                        <td className="py-3 px-4 font-semibold text-red-600">&lt; 0.001</td>
                        <td className="py-3 px-4 text-xs">Chemo+Hormone superior; None shows poorest survival</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Interpretation:</strong> Stage II disease, ER-negative status, and lack of treatment are associated with 
                  significantly worse survival outcomes. The survival curves demonstrate clear separation, with differences emerging within 
                  12-18 months and persisting throughout follow-up.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Method 2: Cox Proportional Hazards */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">4. Cox Proportional Hazards Model</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.1 Method Overview</h3>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed mb-3">
                    The <strong>Cox proportional hazards model</strong> is a semi-parametric regression approach that models the hazard function as:
                  </p>
                  <div className="bg-white rounded p-3 font-mono text-sm text-gray-800 mb-3">
                    h(t | X) = h₀(t) · exp(β₁X₁ + β₂X₂ + ... + βₚXₚ)
                  </div>
                  <p className="text-gray-700 leading-relaxed">
                    The baseline hazard h₀(t) is unspecified, making it flexible. The model estimates <strong>Hazard Ratios (HRs)</strong>: 
                    an HR of 1.5 means a 50% higher instantaneous risk of the event compared to the reference group.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">4.2 Proportional Hazards Assumption</h3>
                <p className="text-gray-700">
                  The model assumes hazard ratios remain constant over time. This is tested using <code className="bg-gray-100 px-2 py-1 rounded text-sm">cox.zph()</code>. 
                  A non-significant p-value (p &gt; 0.05) supports the proportional hazards assumption. Visual inspection via Schoenfeld residual plots 
                  should show horizontal trends.
                </p>
              </div>

              {/* Figure 4: Forest Plot */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 4: Multivariable Cox Model - Forest Plot of Hazard Ratios</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={450}>
                    <ComposedChart 
                      data={coxResults} 
                      layout="vertical"
                      margin={{ top: 20, right: 80, bottom: 20, left: 180 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        type="number" 
                        domain={[0, 4]}
                        ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4]}
                        label={{ value: 'Hazard Ratio (95% CI)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 11 }}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="variable" 
                        tick={{ fontSize: 11 }}
                        width={170}
                      />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload[0]) {
                            const data = payload[0].payload;
                            return (
                              <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                                <p className="font-semibold text-gray-900 text-sm mb-1">{data.variable}</p>
                                <p className="text-sm text-gray-700">HR: <strong>{data.hr.toFixed(2)}</strong></p>
                                <p className="text-sm text-gray-700">95% CI: {data.ci}</p>
                                <p className="text-sm text-gray-700">p-value: <strong>{data.pValue}</strong></p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <ReferenceLine x={1} stroke="#64748b" strokeWidth={2} strokeDasharray="5 5" label={{ value: 'HR = 1', position: 'top', fill: '#64748b', fontSize: 12 }} />
                      <Bar dataKey="hr" fill="#f97316" radius={[0, 4, 4, 0]}>
                        {coxResults.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.hr < 1 ? '#059669' : entry.hr > 1 ? '#dc2626' : '#94a3b8'} />
                        ))}
                      </Bar>
                      <Line 
                        dataKey="ci_lower" 
                        stroke="#374151" 
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line 
                        dataKey="ci_upper" 
                        stroke="#374151" 
                        strokeWidth={2}
                        dot={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Green bars indicate protective factors (HR &lt; 1), red bars indicate increased risk (HR &gt; 1)
                  </p>
                </div>
              </div>

              {/* Table 5: Cox Results */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table 5: Cox Proportional Hazards Multivariable Results</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-orange-600 to-red-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Variable</th>
                        <th className="text-center py-3 px-4 font-semibold">HR</th>
                        <th className="text-center py-3 px-4 font-semibold">95% CI</th>
                        <th className="text-center py-3 px-4 font-semibold">p-value</th>
                        <th className="text-left py-3 px-4 font-semibold">Interpretation</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {coxResults.map((result, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0 hover:bg-orange-50 transition-colors">
                          <td className="py-3 px-4 font-medium text-gray-900">{result.variable}</td>
                          <td className="py-3 px-4 text-center font-semibold">
                            <span className={result.hr < 1 ? 'text-green-700' : result.hr > 1 ? 'text-red-700' : 'text-gray-700'}>
                              {result.hr.toFixed(2)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-center text-gray-700">{result.ci}</td>
                          <td className="py-3 px-4 text-center font-semibold text-blue-700">{result.pValue}</td>
                          <td className="py-3 px-4 text-gray-700 text-xs">
                            {result.hr < 1 ? `${((1 - result.hr) * 100).toFixed(0)}% reduction in hazard` : 
                             result.hr > 1 ? `${((result.hr - 1) * 100).toFixed(0)}% increase in hazard` : 
                             'No effect'}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 border-t-2 border-blue-300">
                        <td className="py-3 px-4 font-semibold text-gray-900" colSpan="2">C-statistic (OS model)</td>
                        <td className="py-3 px-4 text-center font-bold text-blue-700" colSpan="3">{cStatistic.toFixed(3)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Interpretation:</strong> After adjusting for all covariates, Stage II disease (HR=2.34), Grade 3 tumours (HR=1.89), 
                  HER2-positive status (HR=1.45), and higher lymph node burden (HR=1.18 per node) independently increase mortality risk. 
                  Combined chemo-hormone therapy provides substantial protection (HR=0.48, 52% risk reduction). The model demonstrates good discrimination (C-statistic=0.78).
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Method 3: Competing Risks */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">5. Competing Risks Analysis</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">5.1 Why Competing Risks?</h3>
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Standard Cox regression treats deaths from other causes as <em>censored</em>, assuming they are uninformative. 
                    This can <strong>overestimate cancer-specific mortality</strong>, particularly in older cohorts where competing causes 
                    (cardiovascular disease, other cancers) are common.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Competing risks analysis</strong> explicitly acknowledges that experiencing one event (e.g., cardiovascular death) 
                    precludes experiencing the other (cancer death), providing more accurate absolute risk estimates.
                  </p>
                </div>
              </div>

              {/* Table 6: Comparison */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table 6: Cause-Specific Cox vs Fine-Gray. When to Use Each</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Approach</th>
                        <th className="text-left py-3 px-4 font-semibold">What It Estimates</th>
                        <th className="text-left py-3 px-4 font-semibold">When to Use</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {competingRisksComparison.map((comp, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0">
                          <td className="py-3 px-4 font-medium text-gray-900">{comp.approach}</td>
                          <td className="py-3 px-4 text-gray-700">{comp.estimates}</td>
                          <td className="py-3 px-4 text-gray-700">{comp.whenToUse}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Figure 5: CIF Plot */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 5: Cumulative Incidence Functions by Stage</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={450}>
                    <LineChart data={cifData} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="time" 
                        label={{ value: 'Time (Months)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        domain={[0, 0.8]}
                        ticks={[0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8]}
                        label={{ value: 'Cumulative Incidence', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        formatter={(value) => (value * 100).toFixed(1) + '%'}
                        labelFormatter={(label) => `Month ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="stageI_cancer" 
                        stroke="#2563eb" 
                        strokeWidth={3}
                        name="Stage I - Cancer Death"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="stageI_other" 
                        stroke="#93c5fd" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Stage I - Other Cause"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="stageII_cancer" 
                        stroke="#dc2626" 
                        strokeWidth={3}
                        name="Stage II - Cancer Death"
                        dot={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="stageII_other" 
                        stroke="#fca5a5" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Stage II - Other Cause"
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Solid lines: cancer-specific death. Dashed lines: other-cause death. Stage II shows higher cancer-specific CIF.
                  </p>
                </div>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Interpretation:</strong> At 10 years, Stage II patients have a 76% cumulative incidence of cancer death vs 52% for Stage I. 
                  Other-cause mortality contributes ~21-26% across stages, highlighting the importance of not treating these deaths as simple censoring events. 
                  Fine-Gray analysis (sHR for Stage II = 2.18, p&lt;0.001) accounts for competing risks in risk prediction.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Method 4: Time-varying Covariates */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">6. Time-Varying Covariate Analysis</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">6.1 The Problem with Baseline-Only Models</h3>
                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Standard Cox models assume covariates are <strong>fixed at baseline</strong>. However, biomarkers like CA 15-3 change dynamically 
                    during follow-up, and rising levels often precede disease recurrence or death.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Time-varying covariate analysis</strong> captures these dynamic changes by splitting follow-up into intervals, 
                    updating covariate values at each measurement. This uses the <em>counting process format</em> (tstart/tstop).
                  </p>
                </div>
              </div>

              {/* Table 7: Counting process example */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table 7: Counting Process Format. Example Patient</h3>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-sm text-gray-700 mb-3">Patient 001 had rising CA 15-3, died at month 38:</p>
                  <table className="w-full text-sm border border-gray-300 rounded-lg overflow-hidden">
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="text-left py-2 px-4 font-semibold">Patient</th>
                        <th className="text-left py-2 px-4 font-semibold">tstart</th>
                        <th className="text-left py-2 px-4 font-semibold">tstop</th>
                        <th className="text-left py-2 px-4 font-semibold">CA 15-3</th>
                        <th className="text-left py-2 px-4 font-semibold">event</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      <tr className="border-b border-gray-200">
                        <td className="py-2 px-4">001</td>
                        <td className="py-2 px-4">0</td>
                        <td className="py-2 px-4">6</td>
                        <td className="py-2 px-4 font-mono">22.1</td>
                        <td className="py-2 px-4">0</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="py-2 px-4">001</td>
                        <td className="py-2 px-4">6</td>
                        <td className="py-2 px-4">12</td>
                        <td className="py-2 px-4 font-mono">25.4</td>
                        <td className="py-2 px-4">0</td>
                      </tr>
                      <tr>
                        <td className="py-2 px-4">001</td>
                        <td className="py-2 px-4">12</td>
                        <td className="py-2 px-4 font-semibold text-red-600">38</td>
                        <td className="py-2 px-4 font-mono font-semibold text-red-600">41.7</td>
                        <td className="py-2 px-4 font-semibold text-red-600">1</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Figure 6: Time-varying CA 15-3 */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 6: CA 15-3 Trajectories by Risk Group</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={timeVaryingData} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="time" 
                        label={{ value: 'Time (Months)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        label={{ value: 'CA 15-3 (U/mL)', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        labelFormatter={(label) => `Month ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="ca153_low" 
                        stroke="#059669" 
                        strokeWidth={3}
                        name="Low Risk (Stable)"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="ca153_medium" 
                        stroke="#f59e0b" 
                        strokeWidth={3}
                        name="Medium Risk (Moderate Rise)"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="ca153_high" 
                        stroke="#dc2626" 
                        strokeWidth={3}
                        name="High Risk (Rapid Rise)"
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    HR for CA 15-3 (time-varying Cox model): 1.04 per U/mL (95% CI: 1.02–1.06, p &lt; 0.001)
                  </p>
                </div>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Interpretation:</strong> Each 1 U/mL increase in CA 15-3 during follow-up is associated with a 4% increase in 
                  subsequent mortality hazard (adjusted for baseline characteristics). Patients with rapidly rising CA 15-3 (red line) demonstrate 
                  the highest risk, validating CA 15-3 as a dynamic monitoring marker independent of baseline clinical factors.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Method 5: Linear Mixed Effects */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl">
                <Activity className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">7. Linear Mixed-Effects Model. Longitudinal CA 15-3</h2>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">7.1 Why Mixed Effects for Longitudinal Data?</h3>
                <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                  <p className="text-gray-700 leading-relaxed mb-3">
                    Repeated measurements from the same patient are <strong>correlated</strong>. Standard linear regression assumes independence, 
                    leading to underestimated standard errors and inflated significance.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Linear mixed-effects (LME) models</strong> account for within-patient correlation by including <em>random effects</em> 
                    (random intercepts and slopes), allowing each patient to have their own baseline and trajectory while estimating population-level 
                    (fixed) effects.
                  </p>
                </div>
              </div>

              {/* Table 8: Model hierarchy */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Table 8: Model Hierarchy. Sequential Building</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-pink-600 to-rose-600 text-white">
                      <tr>
                        <th className="text-left py-3 px-4 font-semibold">Model</th>
                        <th className="text-left py-3 px-4 font-semibold">Formula</th>
                        <th className="text-left py-3 px-4 font-semibold">Purpose</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {modelHierarchy.map((model, idx) => (
                        <tr key={idx} className="border-b border-gray-200 last:border-0">
                          <td className="py-3 px-4 font-mono font-semibold text-blue-700">{model.model}</td>
                          <td className="py-3 px-4 font-mono text-xs text-gray-700">{model.formula}</td>
                          <td className="py-3 px-4 text-gray-700">{model.purpose}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">7.2 Treatment × Time Interaction</h3>
                <p className="text-gray-700 mb-4">
                  The key question: <strong>Do treatment groups show different CA 15-3 trajectories?</strong>
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span>Negative <code className="bg-gray-100 px-1 rounded">time:treatmentCombined</code> coefficient → CA 15-3 declines faster in combined therapy patients</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span>Positive <code className="bg-gray-100 px-1 rounded">time:treatmentNone</code> coefficient → Untreated patients show rising CA 15-3</span>
                  </li>
                </ul>
              </div>

              {/* Figure 7: LME Trajectories */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Figure 7: Population-Level Predicted CA 15-3 Trajectories by Treatment</h3>
                <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={lmeTrajectories} margin={{ top: 10, right: 30, left: 10, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis 
                        dataKey="time" 
                        label={{ value: 'Time (Months)', position: 'insideBottom', offset: -10, style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis 
                        domain={[40, 160]}
                        label={{ value: 'Predicted CA 15-3 (U/mL)', angle: -90, position: 'insideLeft', style: { fontSize: 14, fontWeight: 600 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip 
                        labelFormatter={(label) => `Month ${label}`}
                        contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.95)', border: '1px solid #cbd5e1', borderRadius: '8px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="hormone" 
                        stroke="#3b82f6" 
                        strokeWidth={2.5}
                        name="Hormone"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="chemo" 
                        stroke="#8b5cf6" 
                        strokeWidth={2.5}
                        name="Chemo"
                        dot={{ r: 4 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="combined" 
                        stroke="#059669" 
                        strokeWidth={3}
                        name="Chemo+Hormone"
                        dot={{ r: 5 }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="none" 
                        stroke="#dc2626" 
                        strokeWidth={2.5}
                        name="None"
                        dot={{ r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  <p className="text-sm text-gray-600 mt-4 text-center">
                    Treatment × Time interaction: F = 18.4, p &lt; 0.001. Combined therapy shows steepest decline.
                  </p>
                </div>
              </div>

              <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Clinical Interpretation:</strong> CA 15-3 declines by ~60% over 24 months in combined therapy patients (from 119 to 48 U/mL) 
                  but increases by ~25% in untreated patients (from 122 to 152 U/mL). The significant treatment×time interaction (p&lt;0.001) confirms 
                  differential biomarker response, validating treatment efficacy and supporting CA 15-3 as a monitoring tool.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Integrated Results & Clinical Narrative */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
                <FileBarChart className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">8. Integrated Results & Clinical Narrative</h2>
            </div>

            <div className="space-y-4">
              <p className="text-gray-700 leading-relaxed">
                This comprehensive analysis combines findings from all five methods to construct a coherent clinical story. Each method contributes 
                a distinct layer of evidence, creating a robust analytical framework:
              </p>

              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold w-1/4">Analysis</th>
                      <th className="text-left py-3 px-4 font-semibold">Clinical Answer</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {integratedNarrative.map((item, idx) => (
                      <tr key={idx} className="border-b border-gray-200 last:border-0 hover:bg-blue-50 transition-colors">
                        <td className="py-4 px-4 font-semibold text-blue-700">{item.analysis}</td>
                        <td className="py-4 px-4 text-gray-700">{item.clinicalAnswer}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-200 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Methodological Triangulation</h3>
                <p className="text-gray-700 text-sm leading-relaxed">
                  This case study demonstrates the value of <strong>methodological triangulation</strong>. using multiple complementary 
                  approaches to answer complex clinical questions. No single method provides the complete picture: Kaplan-Meier offers intuitive 
                  survival visualization, Cox models identify independent risk factors, competing risks prevent bias, time-varying analysis captures 
                  dynamic biomarker changes, and LME models confirm differential treatment response. Together, they form a complete analytical skillset 
                  for modern biostatistics and clinical research.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limitations */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl">
                <AlertCircle className="h-6 w-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">9. Limitations</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Data Limitations</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Synthetic data: true clinical effect sizes may differ</li>
                  <li>• Missing variables: Ki-67, BRCA status, compliance, radiotherapy</li>
                  <li>• Sample size: n=600 with ~40% events</li>
                </ul>
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Methodological Assumptions</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• PH assumption: if violated, requires time-stratified models</li>
                  <li>• Informative censoring: assumed non-informative</li>
                  <li>• LME linearity: assumes linear trajectories</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Clinical Generalizability</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Single-institution cohort structure</li>
                  <li>• Early-stage focus: may not generalize to advanced disease</li>
                  <li>• Treatment protocols: specific to simulated guidelines</li>
                </ul>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">Statistical Considerations</h4>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li>• Multiple testing: no adjustment applied</li>
                  <li>• Model selection: potential overfitting with many predictors</li>
                  <li>• Competing risks: adequate events per category required</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* R Package Reference */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. R Package Reference</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
                <thead className="bg-gradient-to-r from-gray-700 to-gray-900 text-white">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold">Package</th>
                    <th className="text-left py-3 px-4 font-semibold">Analysis</th>
                    <th className="text-left py-3 px-4 font-semibold">Key Functions</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {rPackages.map((pkg, idx) => (
                    <tr key={idx} className="border-b border-gray-200 last:border-0 hover:bg-gray-50 transition-colors">
                      <td className="py-3 px-4 font-mono font-semibold text-blue-700">{pkg.package}</td>
                      <td className="py-3 px-4 text-gray-700">{pkg.analysis}</td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-600">{pkg.keyFunctions}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* What This Delivers for Your Research Team */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">What This Framework Delivers for UAE Hospital Research Teams</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              Statistical methodology is not the goal. publication is. Here is what this analytical framework produces that moves research from data to journal:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-cyan-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">A Methods Section That Passes Peer Review</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    Every analysis in this framework is documented with the statistical tests applied, assumptions checked, R packages used, and interpretation language required for a Methods section that meets international journal standards. The proportional hazards assumption is tested. The competing risks rationale is stated. The mixed-effects model hierarchy is documented. These are the details reviewers look for. and the details that separate a revision request from an acceptance.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Competing Risks Handled Correctly From the Start</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    The Fine-Gray model for competing risks is not a niche technique. it is now expected in any oncology survival analysis where non-cancer deaths are a realistic outcome. Applying standard Cox regression in this setting produces biased estimates that reviewers at oncology journals will identify. Building competing risks analysis into the framework from the start eliminates one of the most common reasons for statistical rejection.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Biomarker Dynamics Captured. Not Just Baseline Values</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    CA 15-3 as a time-varying covariate produced an independent mortality hazard ratio of 1.04 per U/mL (95% CI: 1.02–1.06, p&lt;0.001). a finding invisible to baseline-only models. If your research involves biomarkers measured at multiple timepoints, this is the method that extracts the full predictive value of your longitudinal data rather than reducing it to a single baseline measurement.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Treatment Efficacy Confirmed at the Biomarker Level</h3>
                  <p className="text-gray-700 leading-relaxed text-sm">
                    The mixed-effects model confirmed that CA 15-3 declines by approximately 60% over 24 months in combined therapy patients. and rises by approximately 25% in untreated patients. The treatment x time interaction (F=18.4, p&lt;0.001) validates the biomarker as a treatment monitoring tool, not just a prognostic marker. This level of evidence. biomarker trajectory analysis by treatment group. is what elevates a clinical paper from descriptive to mechanistically informative.
                  </p>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Services This Connects To */}
        <Card className="mb-8 border-0 shadow-lg bg-white/80 backdrop-blur">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Services Used in This Engagement</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">📊</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Research & Academic Support</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Study design and statistical methodology, data analysis using R, SPSS, STATA, and Python, manuscript statistical review for journal submission, results interpretation for non-technical co-authors.
                  </p>
                  <button
                    onClick={() => navigate('/services#research-support')}
                    className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">🔬</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Clinical & Healthcare Analytics</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Patient outcome analysis and benchmarking, survival analysis, longitudinal data modelling, clinical data audit and quality assessment.
                  </p>
                  <button
                    onClick={() => navigate('/services#clinical-analytics')}
                    className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">🤖</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Predictive Modelling & Advanced Analytics</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Multivariable risk modelling, competing risks analysis, time-varying covariate modelling, model discrimination and calibration assessment.
                  </p>
                  <button
                    onClick={() => navigate('/services#predictive-modelling')}
                    className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="text-3xl mb-3">📋</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Publications & Research Support</h3>
                  <p className="text-gray-700 mb-4 leading-relaxed text-sm">
                    Systematic review and meta-analysis support, manuscript statistical review, results interpretation in journal-ready language, statistical methodology documentation for Methods sections.
                  </p>
                  <button
                    onClick={() => navigate('/services#publications-support')}
                    className="text-cyan-600 hover:text-cyan-700 font-semibold text-sm"
                  >
                    Learn more →
                  </button>
                </CardContent>
              </Card>
            </div>
          </CardContent>
        </Card>

        {/* Conclusion */}
        <Card className="mb-8 border-0 shadow-lg bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
          <CardContent className="p-8">
            <h2 className="text-3xl font-bold mb-4">Conclusion</h2>
            <p className="text-lg leading-relaxed text-blue-50 mb-4">
              This case study demonstrates a comprehensive, reproducible analytical framework for oncology research, progressing from 
              intuitive survival visualization through increasingly sophisticated methods that capture competing risks, dynamic biomarker 
              changes, and differential treatment responses.
            </p>
            <p className="text-lg leading-relaxed text-blue-50 mb-6">
              Each method builds upon the previous, addressing limitations and adding clinical nuance. The result is a complete analytical 
              pipeline suitable for health data science, medical statistics consultancy, and academic biostatistics portfolios.
            </p>
            <div className="flex flex-wrap gap-3">
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                Portfolio Project
              </Badge>
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                R Statistical Analysis
              </Badge>
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                April 2026
              </Badge>
              <Badge className="bg-white/20 hover:bg-white/30 text-white border-white/30 px-4 py-2">
                Version 1.0
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="border-0 shadow-lg bg-gradient-to-r from-blue-50 to-cyan-50">
          <CardContent className="p-8 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Is Your Research Team Ready to Publish?
            </h3>
            <p className="text-gray-700 mb-4 max-w-2xl mx-auto">
              If your hospital has clinical data and a research question but lacks the statistical capacity to execute at peer review standard. that is exactly the gap we fill. Whether you need a complete analytical framework from scratch, a methodology review before submission, or statistical support at the revision stage. we work with your data and your clinical team to get the research to publication.
            </p>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Book a free 30-minute consultation. Bring your research question, your dataset structure, and your target journal. We will tell you exactly what the analysis requires and what the timeline looks like.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => navigate('/contact')}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white px-8"
              >
                Book a Free Consultation
              </Button>
              <Button 
                onClick={() => navigate('/services')}
                size="lg"
                variant="outline"
                className="border-2 border-cyan-600 text-cyan-600 hover:bg-cyan-50 px-8"
              >
                View Our Research Support Services
              </Button>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
};

export default OncologySurvivalCaseStudy;
